import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Trash2, Upload, DollarSign } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const ProfileSettings = () => {
  const { user, setUser } = useAuth();
  const fileInputRef = useRef(null);
  const [avatarDisplayUrl, setAvatarDisplayUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hourlyRate, setHourlyRate] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    if (user?.hourly_rate != null) {
      setHourlyRate(parseFloat(user.hourly_rate).toString());
    }
  }, [user?.hourly_rate]);

  const loadAvatarUrl = useCallback(async () => {
    if (!user?.avatar_url) {
      setAvatarDisplayUrl(null);
      return;
    }
    // If it's already a full URL (Google OAuth or presigned), use directly
    if (user.avatar_url.startsWith('http')) {
      setAvatarDisplayUrl(user.avatar_url);
      return;
    }
    // Otherwise fetch presigned URL
    try {
      const res = await api.get('/auth/me/avatar-url');
      setAvatarDisplayUrl(res.data.avatar_url);
    } catch {
      setAvatarDisplayUrl(null);
    }
  }, [user?.avatar_url]);

  useEffect(() => {
    loadAvatarUrl();
  }, [loadAvatarUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate on client side too
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a JPEG, PNG, GIF, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/auth/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Update auth context with new avatar path
      const updatedUser = { ...user, avatar_url: res.data.avatar_url };
      setUser(updatedUser);
      setAvatarDisplayUrl(res.data.avatar_display_url);
      setSuccess('Profile picture updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setError(null);
    setSuccess(null);

    try {
      await api.delete('/auth/me/avatar');
      const updatedUser = { ...user, avatar_url: null };
      setUser(updatedUser);
      setAvatarDisplayUrl(null);
      setSuccess('Profile picture removed');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove image');
    }
  };

  const saveHourlyRate = async () => {
    setSavingRate(true);
    setError(null);
    setSuccess(null);
    try {
      const rate = hourlyRate ? parseFloat(hourlyRate) : null;
      await api.patch('/auth/me/hourly-rate', { hourly_rate: rate });
      setUser({ ...user, hourly_rate: rate });
      setSuccess('Default hourly rate updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update hourly rate');
    } finally {
      setSavingRate(false);
    }
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Profile</h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage your profile picture and personal information
        </p>
      </div>

      <div className="px-6 py-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
            {success}
          </div>
        )}

        <div className="flex items-start gap-6">
          {/* Avatar preview */}
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-gray-200">
              {avatarDisplayUrl ? (
                <img
                  src={avatarDisplayUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-semibold text-gray-500">{initials}</span>
              )}
            </div>
            {/* Overlay on hover */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute inset-0 w-24 h-24 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <Camera className="h-6 w-6 text-white" />
            </button>
          </div>

          {/* Upload controls */}
          <div className="flex-1 pt-2">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>

              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload photo
                    </>
                  )}
                </button>
                {avatarDisplayUrl && (
                  <button
                    onClick={handleRemove}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                )}
              </div>

              <p className="text-xs text-gray-400">
                JPG, PNG, GIF, or WebP. Max 5MB. Recommended 200x200px or larger.
              </p>
            </div>
          </div>
        </div>

        {/* Default Hourly Rate */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <DollarSign className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Default Hourly Rate</h4>
              <p className="text-sm text-gray-500 mb-3">
                This rate is used as the default when creating invoice line items assigned to you.
              </p>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    placeholder="0.00"
                    className="w-36 pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/hr</span>
                </div>
                <button
                  onClick={saveHourlyRate}
                  disabled={savingRate}
                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 disabled:opacity-50"
                >
                  {savingRate ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
